import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Chip,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  CalendarMonth as CalendarIcon,
  LocalOffer as DiscountIcon,
  Person as PersonIcon
} from '@mui/icons-material';

import { 
  getPaymentDetails, 
  PaymentWithItems 
} from '../../../services/paymentService';
import { getAllDiscountCodes, DiscountCode } from '../../../services/discountCodeService';
import { getSessionDetails } from '../../../services/sessionService';

interface PaymentDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  paymentId: number;
}

const PaymentDetailsDialog: React.FC<PaymentDetailsDialogProps> = ({
  open,
  onClose,
  paymentId
}) => {
  const [payment, setPayment] = useState<PaymentWithItems | null>(null);
  const [discountCode, setDiscountCode] = useState<DiscountCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionNames, setSessionNames] = useState<{[key: number]: string}>({});
  
  // Fetch payment details
  const fetchPaymentDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch payment details
      const paymentDetails = await getPaymentDetails(paymentId);
      console.log('=== FRONTEND PAYMENT DEBUG ===');
      console.log('Payment Details received:', JSON.stringify(paymentDetails, null, 2));
      console.log('User data:', paymentDetails.user);
      console.log('=== END FRONTEND DEBUG ===');
      setPayment(paymentDetails);
      
      // Fetch discount code if payment has one
      if (paymentDetails.discountCodeId) {
        try {
          const allCodes = await getAllDiscountCodes(false);
          const foundCode = allCodes.find(code => code.id === paymentDetails.discountCodeId);
          if (foundCode) {
            setDiscountCode(foundCode);
          }
        } catch (error) {
          console.error('Error fetching discount code:', error);
        }
      }
      
      // Fetch session names for payment items
      const sessionIds = paymentDetails.items.map(item => item.sessionId);
      const uniqueSessionIds = [...new Set(sessionIds)];
      
      const sessionPromises = uniqueSessionIds.map(async (sessionId) => {
        try {
          const session = await getSessionDetails(sessionId);
          return { id: sessionId, name: session.title };
        } catch (error) {
          console.error(`Error fetching session ${sessionId}:`, error);
          return { id: sessionId, name: `Session ${sessionId}` };
        }
      });
      
      const sessionResults = await Promise.all(sessionPromises);
      const sessionNameMap: {[key: number]: string} = {};
      sessionResults.forEach(result => {
        sessionNameMap[result.id] = result.name;
      });
      
      setSessionNames(sessionNameMap);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      setError('Failed to load payment details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [paymentId]);
  
  // Fetch payment details when component mounts
  useEffect(() => {
    fetchPaymentDetails();
  }, [fetchPaymentDetails]);
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Render payment status chip
  const renderPaymentStatus = (status: string) => {
    const getChipProps = () => {
      switch (status) {
        case 'created':
          return { label: 'Created', color: 'default' as const };
        case 'approved':
          return { label: 'Approved', color: 'primary' as const };
        case 'captured':
          return { label: 'Completed', color: 'success' as const };
        case 'pending':
          return { label: 'Pending', color: 'warning' as const };
        case 'denied':
        case 'expired':
          return { label: status === 'denied' ? 'Denied' : 'Expired', color: 'error' as const };
        case 'refunded':
          return { label: 'Refunded', color: 'secondary' as const };
        default:
          return { label: status, color: 'default' as const };
      }
    };
  
    const { label, color } = getChipProps();
    return <Chip size="small" label={label} color={color} />;
  };
  
  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (error || !payment) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error">{error || 'Payment not found'}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ReceiptIcon sx={{ mr: 1 }} />
          Payment Details
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* User Information */}
          {payment.user && (
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" gutterBottom>
                    Customer Information
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {`${payment.user.firstName} ${payment.user.lastName}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {payment.user.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      User ID
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {payment.user.id}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Payment Information */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Payment Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Payment ID
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {payment.id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    PayPal Order ID
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {payment.paypalOrderId || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="h6" gutterBottom color="primary">
                    {formatCurrency(payment.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {renderPaymentStatus(payment.paymentStatus)}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {payment.paymentMethod === 'paypal' ? 'PayPal' : payment.paymentMethod}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Transaction Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(payment.transactionDate).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Discount Code */}
          {discountCode && (
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DiscountIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" gutterBottom>
                    Discount Code Applied
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Code
                    </Typography>
                    <Typography variant="body1" gutterBottom fontFamily="monospace" fontWeight="bold">
                      {discountCode.code}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Discount
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {discountCode.discountType === 'percentage' 
                        ? `${discountCode.discountValue}%` 
                        : formatCurrency(discountCode.discountValue)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {discountCode.description}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
          
          {/* Payment Items */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Items
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Session</TableCell>
                      <TableCell align="right">Base Price</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="right">Final Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payment.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                            <Typography>
                              {sessionNames[item.sessionId] || `Session ${item.sessionId}`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                        <TableCell align="right">
                          {item.discountApplied > 0 
                            ? formatCurrency(item.discountApplied) 
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.amount - item.discountApplied)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2} />
                      <TableCell align="right">
                        <Typography variant="subtitle2">Total Discount:</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(
                          payment.items.reduce((sum, item) => sum + item.discountApplied, 0)
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={2} />
                      <TableCell align="right">
                        <Typography variant="subtitle1">Total:</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle1" fontWeight="bold">
                          {formatCurrency(payment.amount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDetailsDialog;